/**
 * @file poStatusMove.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-14
 * @modified 2026-10-14
 * @description How a purchase order's status may move once its customer
 *              order is delivered (or invoiced) — the one rule, for the PO
 *              editor, the PO save and the bulk status change alike (Cam,
 *              14 Oct 2026: PO-D10279 sat at Draft after delivery, and could
 *              not be marked confirmed, sent and then billed).
 *
 *   FORWARD ONLY, along Draft → Authorised → Sent → Confirmed. Moving forward
 *   is a record of what happened ("mark as"): it changes no quantity, cost or
 *   line, and sends the supplier nothing — no email, no revised PO.
 *   Never backwards. Never to Billed by hand (a linked bill sets BILLED), and
 *   a billed PO stays billed. Never to Archived through a status change
 *   (Archive does that). Statuses are compared case-insensitively — Draft and
 *   DRAFT are both stored.
 *
 *   Written once here; utils/poStatusMove.js on the server is GENERATED from
 *   it by scripts/sync-shared-utils.js.
 */

/** The steps a PO made here walks, in order. */
export const PO_STATUS_STEPS = ['DRAFT', 'AUTHORISED', 'SENT', 'CONFIRMED'];

const LABELS = {
  DRAFT: 'Draft',
  AUTHORISED: 'Authorised',
  SENT: 'Sent',
  CONFIRMED: 'Confirmed',
  BILLED: 'Billed',
  DELETED: 'Archived',
  VOIDED: 'Voided',
  SUBMITTED: 'Submitted'
};

/** A status as stored, uppercase; nothing stored reads as Draft (as the PO save writes it). */
export function normaliseStatusForMove(status) {
  const s = String(status || '').trim().toUpperCase();
  return s || 'DRAFT';
}

/** How a status reads in a sentence. */
export function statusMoveLabel(status) {
  const s = normaliseStatusForMove(status);
  return LABELS[s] || (s.charAt(0) + s.slice(1).toLowerCase());
}

/**
 * Why a PO whose order is delivered (or invoiced) may not move from one
 * status to another, or null when it may (including no move at all).
 *
 * @param {string} from — the status stored
 * @param {string} to — the status asked for
 * @returns {string|null}
 */
export function statusMoveAfterDelivery(from, to) {
  const a = normaliseStatusForMove(from);
  const b = normaliseStatusForMove(to);
  if (a === b) return null;
  const move = `${statusMoveLabel(a)} → ${statusMoveLabel(b)}`;
  if (a === 'BILLED') return `the status stays Billed — a linked bill sets it (${move})`;
  if (a === 'DELETED' || a === 'VOIDED') return `the status of an archived PO is not changed — restore it first (${move})`;
  if (b === 'BILLED') return `Billed is set by adding the bill, not by hand (${move})`;
  if (b === 'DELETED' || b === 'VOIDED') return `archive it with Archive, not a status change (${move})`;
  const i = PO_STATUS_STEPS.indexOf(a);
  const j = PO_STATUS_STEPS.indexOf(b);
  if (j < 0) return `${statusMoveLabel(b)} is not a status a PO can be given (${move})`;
  if (i < 0) return `the status can only move forward once its order is delivered, and ${statusMoveLabel(a)} is not one of Draft, Authorised, Sent, Confirmed (${move})`;
  if (j < i) return `the status can only move forward once its order is delivered: ${move}`;
  return null;
}

/** The statuses a delivered order's PO may be given from `from`: itself, then the forward steps. */
export function statusesAfterDelivery(from) {
  const a = normaliseStatusForMove(from);
  return [a, ...PO_STATUS_STEPS.filter(s => s !== a && !statusMoveAfterDelivery(a, s))];
}

